import React from 'react';
import { useAddLayout } from '../hooks/useAddLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/shared/ui/custom/searchable-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export const AddLayoutForm: React.FC = () => {
  const {
    clients,
    projects,
    layoutTypes,
    formData,
    isLoadingClients,
    isLoadingProjects,
    isSubmitting,
    error,
    successMessage,
    handleClientChange,
    handleProjectChange,
    handleNameChange,
    handleTypeChange,
    handleSubmit,
  } = useAddLayout();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  const clientOptions = React.useMemo(
    () =>
      clients.map((client) => ({
        value: client.id.toString(),
        label: client.name,
      })),
    [clients]
  );

  return (
    <div className="w-full max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Add Layout</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-6">
            {/* Client Select */}
            <div className="space-y-2">
              <Label htmlFor="client">Client</Label>
              {isLoadingClients ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading clients...
                </div>
              ) : (
                <SearchableSelect
                  options={clientOptions}
                  value={formData.client?.toString() || ''}
                  onValueChange={(value) => handleClientChange(value ? Number(value) : null)}
                  placeholder="Select a client"
                  searchPlaceholder="Search clients..."
                  emptyMessage="No clients with active projects found."
                />
              )}
            </div>

            {/* Project Select */}
            <div className="space-y-2">
              <Label htmlFor="project">Project</Label>
              {isLoadingProjects ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading projects...
                </div>
              ) : (
                <Select
                  value={formData.project?.toString() || ''}
                  onValueChange={(value) => handleProjectChange(value ? Number(value) : null)}
                  disabled={!formData.client || projects.length === 0}
                >
                  <SelectTrigger id="project">
                    <SelectValue 
                      placeholder={
                        !formData.client 
                          ? "Select a client first" 
                          : projects.length === 0 
                            ? "No active projects for selected client"
                            : "Select a Project"
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {projects.length === 0 ? (
                      <div className="px-2 py-6 text-sm text-center text-muted-foreground">
                        No active projects available
                      </div>
                    ) : (
                      projects.map((project) => (
                        <SelectItem key={project.id} value={project.id.toString()}>
                          {project.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Layout Name Input */}
            <div className="space-y-2">
              <Label htmlFor="name">Layout Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter layout name"
                className="rounded-lg"
              />
            </div>

            {/* Type Select */}
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  handleTypeChange(value as 'Progress' | 'Asbuild' | 'Corridor' | '')
                }
              >
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select a Type" />
                </SelectTrigger>
                <SelectContent>
                  {layoutTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Success Message */}
            {successMessage && (
              <Alert className="border-primary/30 bg-primary/10">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <AlertDescription className="text-foreground">
                  {successMessage}
                </AlertDescription>
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={
                isSubmitting ||
                !formData.client ||
                !formData.project ||
                !formData.name ||
                !formData.type
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding Layout...
                </>
              ) : (
                'Add Layout'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

